import React, { useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Route, Switch, RouteComponentProps } from 'react-router-dom';
import { media } from '../theme/tokens';
import { KpiStrip } from '../components/KpiStrip';
import { DataTable } from '../components/DataTable';
import { SimpleSelect } from '../components/SimpleSelect';
import { PeriodToggle } from '../components/PeriodToggle';
import { PageHeader } from '../components/PageHeader';
import { TabBar } from '../components/TabBar';
import { SearchInput } from '../components/SearchInput';
import { useNotationHeader, useNotationRaw } from 'apiTyped/hooks/notes.query';
import { CompareView } from './NotesCompare';
import { WxrView } from './NotesWxr';

// --- View Tabs (matches V6 prototype .view-tabs pattern) ---

type NotesTab = 'notes' | 'compare' | 'wxr';

interface ViewTabDef {
  key: NotesTab;
  label: string;
  path: string;
}

const TABS: ViewTabDef[] = [
  { key: 'notes', label: 'All Notes', path: '' },
  { key: 'compare', label: 'Compare', path: '/compare' },
  { key: 'wxr', label: 'Wine Expert Rating', path: '/wxr' }
];

// --- Notes Page (container with tabs) ---

export const V2Notes: React.FC<RouteComponentProps> = ({
  match,
  history,
  location
}) => {
  const { path, url } = match;

  const activeTab: NotesTab = location.pathname.endsWith('/compare')
    ? 'compare'
    : location.pathname.endsWith('/wxr')
      ? 'wxr'
      : 'notes';

  return (
    <Page>
      <PageHeader title="Notes" subtitle="All wine ratings and tasting notes" />

      {/* View Tabs */}
      <TabBar
        tabs={TABS.map((tab) => ({ id: tab.key, label: tab.label }))}
        activeTab={activeTab}
        onTabChange={(id) => {
          const tab = TABS.find((t) => t.key === id);
          if (tab) history.push(`${url}${tab.path}`);
        }}
      />

      <Switch>
        <Route exact path={path} component={AllNotesView} />
        <Route path={`${path}/compare`} component={CompareView} />
        <Route path={`${path}/wxr`} component={WxrView} />
      </Switch>
    </Page>
  );
};

// CompareView and WxrView imported from ./NotesCompare and ./NotesWxr

// --- All Notes (existing table view, extracted into its own component) ---

const AllNotesView: React.FC = () => {
  const { isLoading: isLoadingHeader, header } = useNotationHeader();
  const { isLoading: isLoadingRaw, raw } = useNotationRaw();

  // Filter state
  const [wineFilter, setWineFilter] = useState<string>('');
  const [vintageFilter, setVintageFilter] = useState<string>('');
  const [journalFilter, setJournalFilter] = useState<string>('');
  const [tasterFilter, setTasterFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAll, setShowAll] = useState(false);

  const data = (raw || []) as any[];

  // Extract filter options from raw data
  const filterOptions = useMemo(() => {
    const wines = [...new Set(data.map((item: any) => item.wine_name))]
      .filter(Boolean)
      .sort();
    const vintages = [...new Set(data.map((item: any) => item.vintage))]
      .filter(Boolean)
      .sort()
      .reverse();
    const journals = [...new Set(data.map((item: any) => item.journal_name))]
      .filter(Boolean)
      .sort();
    const tasters = [...new Set(data.map((item: any) => item.degustateur_name))]
      .filter(Boolean)
      .sort();
    return { wines, vintages, journals, tasters };
  }, [data]);

  // Apply filters
  const filteredData = useMemo(() => {
    let result = [...data];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (item: any) =>
          (item.wine_name || '').toLowerCase().includes(term) ||
          (item.journal_name || '').toLowerCase().includes(term) ||
          (item.degustateur_name || '').toLowerCase().includes(term) ||
          (item.vintage || '').toString().includes(term)
      );
    }

    if (wineFilter) {
      result = result.filter((item: any) => item.wine_name === wineFilter);
    }
    if (vintageFilter) {
      result = result.filter((item: any) => item.vintage === vintageFilter);
    }
    if (journalFilter) {
      result = result.filter(
        (item: any) => item.journal_name === journalFilter
      );
    }
    if (tasterFilter) {
      result = result.filter(
        (item: any) => item.degustateur_name === tasterFilter
      );
    }

    return showAll ? result : result.slice(0, 100);
  }, [
    data,
    searchTerm,
    wineFilter,
    vintageFilter,
    journalFilter,
    tasterFilter,
    showAll
  ]);

  // KPI data
  const ratings = (header as any)?.ratings || [];
  const totalNotes = data.length;
  const uniqueWines = new Set(data.map((item: any) => item.wine_name)).size;
  const uniqueJournals = new Set(data.map((item: any) => item.journal_name))
    .size;

  const handleResetFilters = useCallback(() => {
    setWineFilter('');
    setVintageFilter('');
    setJournalFilter('');
    setTasterFilter('');
    setSearchTerm('');
  }, []);

  const isLoading = isLoadingHeader || isLoadingRaw;

  const columns = useMemo(
    () => [
      {
        key: 'date',
        header: 'Date',
        sortable: true,
        width: '10%',
        render: (row: any) =>
          row.date ? moment(row.date).format('DD/MM/YYYY') : '—'
      },
      {
        key: 'wine_name',
        header: 'Wine',
        sortable: true,
        width: '22%',
        render: (row: any) => <WineName>{row.wine_name || '—'}</WineName>
      },
      {
        key: 'vintage',
        header: 'Vintage',
        sortable: true,
        width: '8%',
        render: (row: any) => row.vintage || '—'
      },
      {
        key: 'journal_name',
        header: 'Journal',
        sortable: true,
        width: '15%',
        render: (row: any) => row.journal_name || '—'
      },
      {
        key: 'degustateur_name',
        header: 'Taster',
        sortable: true,
        width: '15%',
        render: (row: any) => row.degustateur_name || '—'
      },
      {
        key: 'note_filtered',
        header: 'Score',
        sortable: true,
        width: '10%',
        render: (row: any) => <ScoreCell>{row.note_filtered || '—'}</ScoreCell>
      },
      {
        key: 'comment_filtered',
        header: 'Comment',
        width: '20%',
        render: (row: any) => (
          <CommentCell>
            {row.login ? row.comment_filtered || '—' : 'Subscription required'}
          </CommentCell>
        )
      }
    ],
    []
  );

  return (
    <>
      {/* KPI Strip */}
      <KpiStrip
        items={[
          { label: 'Total notes', value: totalNotes },
          { label: 'Wines rated', value: uniqueWines },
          { label: 'Journals', value: uniqueJournals },
          {
            label: 'Showing',
            value: `${filteredData.length}${
              !showAll && data.length > 100 ? ' / ' + data.length : ''
            }`
          }
        ]}
      />

      {/* Filters */}
      <FiltersBar>
        <SearchInput
          placeholder="Search wines, journals, tasters..."
          value={searchTerm}
          onChange={(val) => {
            setSearchTerm(val);
          }}
        />
        <SimpleSelect
          options={[
            { value: '', label: 'All wines' },
            ...filterOptions.wines.map((w) => ({
              value: w as string,
              label: w as string
            }))
          ]}
          value={wineFilter}
          onChange={(val) => setWineFilter(val as string)}
          minWidth="130px"
        />
        <SimpleSelect
          options={[
            { value: '', label: 'All vintages' },
            ...filterOptions.vintages.map((v) => ({
              value: v as string,
              label: v as string
            }))
          ]}
          value={vintageFilter}
          onChange={(val) => setVintageFilter(val as string)}
          minWidth="120px"
        />
        <SimpleSelect
          options={[
            { value: '', label: 'All journals' },
            ...filterOptions.journals.map((j) => ({
              value: j as string,
              label: j as string
            }))
          ]}
          value={journalFilter}
          onChange={(val) => setJournalFilter(val as string)}
          minWidth="130px"
        />
        <SimpleSelect
          options={[
            { value: '', label: 'All tasters' },
            ...filterOptions.tasters.map((t) => ({
              value: t as string,
              label: t as string
            }))
          ]}
          value={tasterFilter}
          onChange={(val) => setTasterFilter(val as string)}
          minWidth="130px"
        />
        {(wineFilter ||
          vintageFilter ||
          journalFilter ||
          tasterFilter ||
          searchTerm) && (
          <ResetButton onClick={handleResetFilters}>Reset</ResetButton>
        )}
      </FiltersBar>

      {/* Data Table */}
      {isLoading ? (
        <LoadingWrap>Loading notes...</LoadingWrap>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={filteredData}
            emptyMessage="No notes found for the current filters."
          />
          {!showAll && data.length > 100 && filteredData.length >= 100 && (
            <ShowAllBar>
              <ShowAllButton onClick={() => setShowAll(true)}>
                Show all {data.length} results
              </ShowAllButton>
            </ShowAllBar>
          )}
        </>
      )}
    </>
  );
};

// --- Styled Components ---

const Page = styled.div`
  max-width: 1400px;
`;

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
`;

const ResetButton = styled.button`
  background: none;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 6px 12px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  color: var(--terracotta);
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: var(--terracotta);
    background: var(--terracotta-soft);
  }
`;

const LoadingWrap = styled.div`
  padding: 48px 0;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
`;

const WineName = styled.span`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: inline-block;
`;

const ScoreCell = styled.span`
  font-weight: 600;
  font-family: var(--font-sans);
`;

const CommentCell = styled.span`
  font-size: 12px;
  color: var(--ink-muted);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ShowAllBar = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const ShowAllButton = styled.button`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-pill);
  padding: 8px 20px;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: var(--terracotta);
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: var(--terracotta);
    background: var(--terracotta-soft);
  }
`;
